import { v4 as uuidv4 } from 'uuid';

export const generatePipelineYaml = (disease, gene) => {
  const uuids = {
    DefineText: {
      gene: { id: uuidv4(), output: uuidv4() },
      disease: { id: uuidv4(), output: uuidv4() },
    },
    queryPubMed: {
      gene: { id: uuidv4(), output: uuidv4() },
      disease: { id: uuidv4(), output: uuidv4() },
    },
    queryPubTator: { id: uuidv4(), output: uuidv4() },
    pubCredibility: { id: uuidv4(), output: uuidv4() },
    topicClustering: { id: uuidv4(), output: uuidv4() },
    tableJoin: {
      main: { id: uuidv4(), output: uuidv4() },
      gene: { id: uuidv4(), output: uuidv4() },
    },
    appendTables: { id: uuidv4(), output: uuidv4() },
    importData: {
      publications: { id: uuidv4() },
      genes: { id: uuidv4() },
    },
    normalizeGeneNames: {
      first: { id: uuidv4(), output: uuidv4() },
      second: { id: uuidv4(), output: uuidv4() },
    },
    tablePivot: {
      id: { id: uuidv4(), output: uuidv4() },
      year: { id: uuidv4(), output: uuidv4() },
    },
    applyLLM: {
      articleCheck: { id: uuidv4(), output: uuidv4() },
      articleInfo: { id: uuidv4(), output: uuidv4() },
      geneAssociation: { id: uuidv4(), output: uuidv4() },
      linkedPubs: { id: uuidv4(), output: uuidv4() },
    },
    jsonToTable: {
      articleOutput: { id: uuidv4(), output: uuidv4() },
      articleColumns: { id: uuidv4(), output: uuidv4() },
      geneAssociations: { id: uuidv4(), output: uuidv4() },
      linkedPubs: { id: uuidv4(), output: uuidv4() },
    },
    filterTable: { id: uuidv4(), output: uuidv4() },
  };

  const result = `components:
  - id: ${uuids.DefineText.gene.id}
    componentType: DefineText
    name: Define Gene
    inputs: {}
    extraParams: []
    outputs:
      result: ${uuids.DefineText.gene.output}
    params:
      text: ${gene}
  - id: ${uuids.queryPubMed.gene.id}
    componentType: QueryPubMed
    name: Query PubMed for ${gene}
    inputs: {}
    extraParams:
      - ${uuids.DefineText.gene.output}
    outputs:
      result: ${uuids.queryPubMed.gene.output}
    params:
      query: $param1
      generateQuery: true
      max_results: 250
  - id: ${uuids.DefineText.disease.id}
    componentType: DefineText
    name: Define Disease
    inputs: {}
    extraParams: []
    outputs:
      result: ${uuids.DefineText.disease.output}
    params:
      text: ${disease}
  - id: ${uuids.queryPubMed.disease.id}
    componentType: QueryPubMed
    name: Query PubMed for ${disease}
    inputs: {}
    extraParams:
      - ${uuids.DefineText.disease.output}
    outputs:
      result: ${uuids.queryPubMed.disease.output}
    params:
      query: $param1
      generateQuery: true
      max_results: 250
  - id: ${uuids.appendTables.id}
    componentType: AppendTables
    name: Append Two Tables
    inputs:
      sourceTable1: ${uuids.queryPubMed.disease.output}
      sourceTable2: ${uuids.queryPubMed.gene.output}
    extraParams: []
    outputs:
      result: ${uuids.appendTables.output}
    params: {}
  - id: ${uuids.queryPubTator.id}
    componentType: QueryPubTator
    name: Query PubTator
    inputs:
      sourceTable: ${uuids.appendTables.output}
    extraParams: []
    outputs:
      result: ${uuids.queryPubTator.output}
    params: {}
  - id: ${uuids.applyLLM.articleCheck.id}
    componentType: ApplyLLMTable
    name: "LLM: Article on ${disease}?"
    inputs:
      sourceTable: ${uuids.queryPubTator.output}
    extraParams:
      - ${uuids.DefineText.disease.output}
    outputs:
      result: ${uuids.applyLLM.articleCheck.output}
    params:
      prompt: >-
        You are a medical research analyzer. Your task is to determine if the
        following Title and Abstract discuss or are related to $param1. 


        Respond ONLY with a JSON object in this exact format:

        {"article_on_disease": "yes"} or {"article_on_disease": "no"}


        Consider the following criteria when analyzing:

        - Primary focus on $param1

        - Mentions of symptoms, treatments, or complications of $param1

        - Epidemiological studies about $param1

        - Clinical trials related to $param1

        - Diagnostic methods for $param1


        Title: {title}

        Abstract: {abstract}


        Remember:

        1. Only respond with the JSON object

        2. No explanations or additional text

        3. Only use "yes" or "no" as values

        4. Maintain the exact JSON format
      response:
        identifier: article_prompt_output
        name: Article Prompt Output
        description: Article Prompt Output
  - id: ${uuids.jsonToTable.articleOutput.id}
    componentType: JsonToTable
    name: Convert LLM output to Column
    inputs:
      sourceTable: ${uuids.applyLLM.articleCheck.output}
    extraParams: []
    outputs:
      result: ${uuids.jsonToTable.articleOutput.output}
    params:
      jsonSourceColumn: article_prompt_output
      transferColumns:
        - id
        - title
        - authors
        - publication_year
        - journal_name
        - abstract
        - mesh_terms
        - publication_type
        - mentioned_genes
        - mentioned_variants
        - mentioned_chemicals
        - mentioned_diseases
        - mentioned_species
      jsonColumns:
        - id: ${uuidv4()}
          columnIdentifier: article_on_disease
          columnName: Article on ${disease} (LLM)
          columnDescription: LLM based value if the pubmed article actually talks about
            ${disease}
          columnDataType: categorical
  - id: ${uuids.pubCredibility.id}
    componentType: PublicationCredibility
    name: Publication Credibility
    inputs:
      sourceTable: ${uuids.jsonToTable.articleOutput.output}
    extraParams: []
    outputs:
      result: ${uuids.pubCredibility.output}
    params: {}
  - id: ${uuids.topicClustering.id}
    componentType: TopicClustering
    name: Cluster Publications by Topic
    inputs:
      sourceTable: ${uuids.jsonToTable.articleOutput.output}
    extraParams: []
    outputs:
      result: ${uuids.topicClustering.output}
    params: {}
  - id: ${uuids.tableJoin.main.id}
    componentType: TableJoin
    name: Join Tables
    inputs:
      sourceTable1: ${uuids.topicClustering.output}
      sourceTable2: ${uuids.pubCredibility.output}
    extraParams: []
    outputs:
      result: ${uuids.tableJoin.main.output}
    params:
      matchColumn1: id
      matchColumn2: id
      joinType: left
      columnsTable1:
        - topics
        - umap_x
        - umap_y
      columnsTable2:
        - title
        - authors
        - publication_year
        - journal_name
        - abstract
        - mesh_terms
        - publication_type
        - mentioned_genes
        - mentioned_variants
        - mentioned_diseases
        - mentioned_chemicals
        - mentioned_species
        - citations
        - jcr_journal_impact_factor
        - scimago_journal_rank
        - credibility
        - article_on_disease
  - id: ${uuids.normalizeGeneNames.first.id}
    componentType: NormalizeGeneNames
    name: Normalize Gene Names
    inputs:
      sourceTable: ${uuids.tableJoin.main.output}
    extraParams: []
    outputs:
      result: ${uuids.normalizeGeneNames.first.output}
    params:
      geneNamesInput: mentioned_genes
  - id: ${uuids.importData.publications.id}
    componentType: ImportDataContext
    name: Import Publications in Bio|Verse
    inputs:
      sourceTable: ${uuids.normalizeGeneNames.first.output}
    extraParams: []
    outputs: {}
    params:
      entityType: etl_publications
      datatableId: publications_data_table
  - id: ${uuids.tablePivot.id.id}
    componentType: TablePivot
    name: Pivot Table on ID
    inputs:
      sourceTable: ${uuids.normalizeGeneNames.first.output}
    extraParams: []
    outputs:
      result: ${uuids.tablePivot.id.output}
    params:
      groupByColumns:
        - mentioned_genes_normalized
      dropEmptyValues: true
      pivotColumn: mentioned_genes_normalized
      cellValuesColumn: id
      aggType: distinctList
      aggParams: {}
      pivotColumns: []

  - id: ${uuids.tablePivot.year.id}
    componentType: TablePivot
    name: Pivot Table on Year
    inputs:
      sourceTable: ${uuids.normalizeGeneNames.first.output}
    extraParams: []
    outputs:
      result: ${uuids.tablePivot.year.output}
    params:
      groupByColumns:
        - mentioned_genes_normalized
      dropEmptyValues: true
      pivotColumn: mentioned_genes_normalized
      cellValuesColumn: publication_year
      aggType: distinctList
      aggParams: {}
  - id: ${uuids.tableJoin.gene.id}
    componentType: TableJoin
    name: Join Gene Tables
    inputs:
      sourceTable1: ${uuids.tablePivot.year.output}
      sourceTable2: ${uuids.tablePivot.id.output}
    extraParams: []
    outputs:
      result: ${uuids.tableJoin.gene.output}
    params:
      matchColumn1: mentioned_genes_normalized
      matchColumn2: mentioned_genes_normalized
      joinType: left
      columnsTable1:
        - publication_year
      columnsTable2:
        - id
  - id: ${uuids.applyLLM.articleInfo.id}
    componentType: ApplyLLMTable
    name: "Calculate Article info for Gene "
    inputs:
      sourceTable: ${uuids.tableJoin.gene.output}
    extraParams: []
    outputs:
      result: ${uuids.applyLLM.articleInfo.output}
    params:
      prompt: >-
        Process these inputs:

        - Years: {publication_year}

        - IDs: {id}

        - Gene: {mentioned_genes_normalized}


        Return JSON with:

        1. "first_publication": Earliest valid year (1800-present) as "YYYY"

        2. "total_publications": Count of IDs as Integer

        3. "gene_name": Gene name as provided


        Format:

        {"first_publication": YYYY, "total_publications": N, "gene_name":
        "GENE"}


        Rules:

        - All values must be strings

        - Invalid years (< 1800 or > current) are ignored

        - Count must match ID list length exactly


        Example:

        Input:

        - Gene: ACADS

        - Years: [1993, 2008, 2006, 2012, 2000, 2024, 2025, 1999]

        - IDs: [10770178, 10990147, 40048451, 37510852, 10990148, 34254637,
        10931086, 10991888]


        Output:

        {"first_publication": 1993, "total_publications": 8, "gene_name":"ACADS"}
      response:
        identifier: article_info_output
        name: Article Info Output
        description: Article Info Output
  - id: ${uuids.jsonToTable.articleColumns.id}
    componentType: JsonToTable
    name: Convert Article Info to Columns
    inputs:
      sourceTable: ${uuids.applyLLM.articleInfo.output}
    extraParams: []
    outputs:
      result: ${uuids.jsonToTable.articleColumns.output}
    params:
      jsonSourceColumn: article_info_output
      transferColumns:
        - mentioned_genes_normalized
        - publication_year
        - id
      jsonColumns:
        - id: ${uuidv4()}
          columnIdentifier: total_publications
          columnName: Total Publications
          columnDescription: Total Publications
          columnDataType: integer
        - id: ${uuidv4()}
          columnIdentifier: gene_name
          columnName: Gene Name
          columnDescription: Gene Name
          columnDataType: string
        - id: ${uuidv4()}
          columnIdentifier: first_publication
          columnName: First Publication
          columnDescription: First Publication
          columnDataType: integer
  - id: ${uuids.applyLLM.geneAssociation.id}
    componentType: ApplyLLMTable
    name: "LLM: Calculate Gene Association"
    inputs:
      sourceTable: ${uuids.jsonToTable.articleColumns.output}
    extraParams:
      - ${uuids.DefineText.disease.output}
    outputs:
      result: ${uuids.applyLLM.geneAssociation.output}
    params:
      prompt: >-
        Analyze the relationship between Gene {mentioned_genes_normalized} and
        disease $param1. Return a structured JSON response following these
        criteria:


        1. Basic Association:

        - Associated

        - Not Associated (if no association, return basic JSON and stop
        analysis)


        2. Correlation Type:

        - Positive Correlation

        - Negative Correlation


        3. Causality:

        - Cause

        - Prevent


        4. Expression Pattern:

        - Upregulated

        - Downregulated


        5. Evidence Strength:

        - Strong Evidence

        - Moderate Evidence

        - Weak Evidence


        6. Disease Stage Impact:

        - Affects Onset

        - Affects Progression

        - Affects Severity


        7. Therapeutic Relevance:

        - Drug Target

        - Biomarker

        - Treatment Response Marker


        8. Molecular Mechanism:

        - Pathway Involved

        - Complex Formation

        - Regulatory Role


        9. Population Impact:

        - Population Specific

        - Broad Impact


        10. Clinical Application:

        - Diagnostic Marker

        - Prognostic Marker

        - Risk Assessment Marker


        Required JSON format:

        {
            "gene_associations": [],
            "evidence_summary": "",
            "confidence_level": "",
            "key_references": []
        }


        Rules:

        1. Use only ASCII characters in the output

        2. No special quotes or formatting characters

        3. For evidence_summary, use only plain text

        4. confidence_level must be exactly "High", "Medium", or "Low"

        5. key_references should contain only PMID IDs in format "PMID:number"

        6. gene_associations must contain only items from the criteria list
        above

        7. For non-associated genes, return:

        {
            "gene_associations": ["Not Associated"],
            "evidence_summary": "No significant association found in literature",
            "confidence_level": "Low",
            "key_references": []
        }
      response:
        identifier: gene_association_prompt_output
        name: Gene Association Prompt Output
        description: Gene Association Prompt Output
  - id: ${uuids.jsonToTable.geneAssociations.id}
    componentType: JsonToTable
    name: Convert Gene Associations to Columns
    inputs:
      sourceTable: ${uuids.applyLLM.geneAssociation.output}
    extraParams: []
    outputs:
      result: ${uuids.jsonToTable.geneAssociations.output}
    params:
      jsonSourceColumn: gene_association_prompt_output
      transferColumns:
        - mentioned_genes_normalized
        - publication_year
        - id
        - total_publications
        - gene_name
        - first_publication
      jsonColumns:
        - id: ${uuidv4()}
          columnIdentifier: gene_associations
          columnName: Gene Associations
          columnDescription: Gene Associations
          columnDataType: multi_categorical
        - id: ${uuidv4()}
          columnIdentifier: confidence_level
          columnName: Confidence Level
          columnDescription: Confidence Level
          columnDataType: categorical
        - id: ${uuidv4()}
          columnIdentifier: evidence_summary
          columnName: Evidence Summary
          columnDescription: Evidence Summary
          columnDataType: string
  - id: ${uuids.applyLLM.linkedPubs.id}
    componentType: ApplyLLMTable
    name: "LLM: Refactor linked publications"
    inputs:
      sourceTable: ${uuids.jsonToTable.geneAssociations.output}
    extraParams: []
    outputs:
      result: ${uuids.applyLLM.linkedPubs.output}
    params:
      prompt: >-
        You are a data formatter. Convert the following list of IDs into a JSON
        object with a single key "linked_publications" containing all IDs as a
        semicolon-separated string.


        Input format: List of IDs in any format (array, comma-separated, or
        line-separated)

        Output format: {"linked_publications": id1;id2;id3;...}


        Rules:

        1. Only respond with the JSON object

        2. Use semicolons (;) as separators

        3. No spaces between IDs

        4. No trailing semicolon

        5. Maintain the exact JSON format

        6. No additional text or explanations


        Example input:

        [123, 456, 789]

        or

        123

        456

        789

        or

        123,456,789


        Example output:

        {"linked_publications": 123;456;789}


        Convert this list:

        {id}


        Remember: Return only the JSON object, nothing else.
      response:
        identifier: linked_publications_prompt_output
        name: linked_publications_prompt_output
        description: linked_publications_prompt_output
  - id: ${uuids.jsonToTable.linkedPubs.id}
    componentType: JsonToTable
    name: Convert linked publications to column
    inputs:
      sourceTable: ${uuids.applyLLM.linkedPubs.output}
    extraParams: []
    outputs:
      result: ${uuids.jsonToTable.linkedPubs.output}
    params:
      jsonSourceColumn: linked_publications_prompt_output
      transferColumns:
        - publication_year
        - total_publications
        - gene_name
        - first_publication
        - gene_associations
        - confidence_level
        - evidence_summary
      jsonColumns:
        - id: ${uuidv4()}
          columnIdentifier: linked_publications
          columnName: Linked Publications
          columnDescription: Linked Publications
          columnDataType: multi_categorical
  - id: ${uuids.normalizeGeneNames.second.id}
    componentType: NormalizeGeneNames
    name: Lookup Ensembl_Id
    inputs:
      sourceTable: ${uuids.jsonToTable.linkedPubs.output}
    extraParams: []
    outputs:
      result: ${uuids.normalizeGeneNames.second.output}
    params:
      geneNamesInput: gene_name
  - id: ${uuids.filterTable.id}
    componentType: FilterTable
    name: Filter out empty Ensembl_ids
    inputs:
      sourceTable: ${uuids.normalizeGeneNames.second.output}
    extraParams: []
    outputs:
      result: ${uuids.filterTable.output}
    params:
      filters:
        - id: ${uuidv4()}
          colId: ensembl_id
          filterType: neq
          value: ""
          valueType: string
  - id: ${uuids.importData.genes.id}
    componentType: ImportDataContext
    name: Import Genes in Bio|Verse
    inputs:
      sourceTable: ${uuids.filterTable.output}
    extraParams: []
    outputs: {}
    params:
      datatableId: genes_data_table
      entityType: etl_genes

`;
  return result;
};

export const generateDashboardYaml = () => {
  const dashboardYaml = `
templateContent:
  assetType: bioreflect_pipeline
  displayName: Bio|verse Dashboard for bioreflect_results
  elements:
    sections:
    - elements:
      - content: Overview of genes related to the Pubmed Literature Summary, covering gene
          information, associated documents and gene-disease relationships
        type: text
      - elements:
        - tablegenes_table
        type: layoutHorizontal
      - elements:
        - widgetGene_gene_first_year_count
        - widgetGene_gene_disease_relations
        - widgetGene_YearsStrip1
        type: layoutHorizontal
      id: sc1
      title: Genes related to Pubmed Literature Summary
    - elements:
      - content: A summary of literature fetched from Pubmed. It includes an overview of each paper information, credibility,
          gene mentions extracted via Pubtator, and citation counts.
        type: text
      - elements:
        - tablepublications_data_table
        type: layoutHorizontal
      - elements:
        - widgetPaper_authors
        - widgetPaper_journal_title
        - widgetPaper_cred_score
        - widgetPaper_citations
        type: layoutHorizontal
      - elements:
        - widgetPaper_year
        - widgetPaper_disease
        - widgetPaper_chemical
        type: layoutHorizontal
      id: sc2
      title: Pubmed Literature Summary
    - elements:
      - content: A topic modelling visual overview of the paper content, with a content
          promixity based visual embedding.
        type: text
      - elements:
        - facet_topic
        - embedding_umap
        type: layoutHorizontal
      id: sc3
      title: Paper topic modelling
    
    
    
    widgets:
  
      widgetGene_gene_first_year_count:
        chartType: Histogram
        dataBinding:
          dataTableId: genes_data_table
          aspectBindings:
            value: first_publication
        config:
          minVal: 1990
          maxVal: 2025
          fixedBinSize: 1
        layoutProperties:
          fraction: 0.5
          height: 350

      widgetGene_gene_disease_relations:
        chartType: FacetCategorical
        config:
          simpleLayout: true
        dataBinding:
          aspectBindings:
            categories: gene_associations
          dataTableId: genes_data_table
        layoutProperties:
          fraction: 0.5
          height: 350

      widgetGene_YearsStrip1:
        chartType: StripPlot
        config:
          colorGammaNumerical: 2
          labelSpace: 1
          sortBy: alphabetical
        dataBinding:
          aspectBindings:
            categories: first_publication
            color: total_publications
            hoverLabel: gene_name
            values: total_publications
          dataTableId: genes_data_table
        layoutProperties:
          fraction: 1
          height: 350

      tablegenes_table:
        chartType: DataTable
        config:
          columns:
          - bold: true
            id: gene_name
            width: 100
          - id: ensembl_id
            width: 100
          - id: total_publications
            numericalBar:
              maxVal: 300
              minVal: 0
            width: 75
          - id: first_publication
            width: 75
          - categoryColors:
            - backgroundColor:
                b: 230
                g: 230
                r: 192
              content: Associated
            - backgroundColor:
                b: 250
                g: 210
                r: 230
              content: Prevent
            - backgroundColor:
                b: 210
                g: 210
                r: 240
              content: Not Associated
            - backgroundColor:
                b: 050
                g: 050
                r: 050
              content: Drug Target
            - backgroundColor:
                b: 150
                g: 110
                r: 140
              content: Affects Severity
            - backgroundColor:
                b: 100
                g: 210
                r: 140
              content: Moderate Evidence
            - backgroundColor:
                b: 200
                g: 230
                r: 150
              content: Cause
            - backgroundColor:
                b: 192
                g: 230
                r: 220
              content: Positive Correlation
            - backgroundColor:
                b: 140
                g: 110
                r: 220
              content: Pathway Involved
            - backgroundColor:
                b: 100
                g: 110
                r: 020
              content: Upregulated
            - backgroundColor:
                b: 220
                g: 220
                r: 255
              content: Negative Correlation
            id: gene_associations
            useBadge: true
            width: 430
          - categoryColors:
            - backgroundColor:
                b: 0
                g: 0
                r: 204
              content: High
            - backgroundColor:
                b: 51
                g: 153
                r: 255
              content: Medium
            - backgroundColor:
                b: 100
                g: 210
                r: 140
              content: Low
            id: confidence_level
            useBadge: true
            width: 125
          - id: evidence_summary
            width: 250
          sortDir: desc

        dataBinding:
          aspectBindings:
            sortBy: total_publications
          dataTableId: genes_data_table
        layoutProperties:
          fraction: 1
          height: 600

      tablepublications_data_table:
        chartType: DataTable
        config:
          columns:
          - id: id
            width: 100
          - bold: true
            id: title
            width: 400
          - id: publication_year
            width: 75
          - id: article_on_disease
            width: 75
          - id: journal_name
            width: 100
          - id: citations
            numericalBar:
              maxVal: 150
              minVal: 0
            width: 75
          - id: scimago_journal_rank
            numericalBar:
              maxVal: 146
              minVal: 0
            width: 75
          - id: jcr_journal_impact_factor
            numericalBar:
              maxVal: 50
              minVal: 0
            width: 75
          - id: authors
            useBadge: true
            width: 400
          - id: mentioned_diseases
            useBadge: true
            width: 400
          - id: mentioned_variants
            useBadge: true
            width: 400
        dataBinding:
          aspectBindings: {}
          dataTableId: publications_data_table
        layoutProperties:
          fraction: 1
          height: 500
  
      widgetPaper_authors:
        chartType: FacetCategorical
        dataBinding:
          dataTableId: publications_data_table
          aspectBindings:
            categories: authors
        config:
          simpleLayout: false
        layoutProperties:
          fraction: 1
          height: 350

      widgetPaper_year:
        chartType: Histogram
        dataBinding:
          dataTableId: publications_data_table
          aspectBindings:
            value: publication_year
        config:
          minVal: 1990
          maxVal: 2025
          fixedBinSize: 1
        layoutProperties:
          fraction: 0.5
          height: 350
  
      widgetPaper_chemical:
        chartType: FacetCategorical
        config:
          simpleLayout: false
        dataBinding:
          aspectBindings:
            categories: mentioned_chemicals
          dataTableId: publications_data_table
        layoutProperties:
          fraction: 0.25
          height: 350

      widgetPaper_citations:
        chartType: Histogram
        config:
          maxVal: 150
          minVal: 0
          resolution: 10
        dataBinding:
          aspectBindings:
            value: citations
          dataTableId: publications_data_table
        layoutProperties:
          fraction: 1
          height: 350

      widgetPaper_cred_score:
        chartType: Histogram
        config: {}
        dataBinding:
          aspectBindings:
            value: credibility
          dataTableId: publications_data_table
        layoutProperties:
          fraction: 1
          height: 350
      
      widgetPaper_disease:
        chartType: FacetCategorical
        config:
          simpleLayout: false
        dataBinding:
          aspectBindings:
            categories: mentioned_diseases
          dataTableId: publications_data_table
        layoutProperties:
          fraction: 0.25
          height: 350

      widgetPaper_gene:
        chartType: FacetCategorical
        config:
          simpleLayout: false
        dataBinding:
          aspectBindings:
            categories: mentioned_genes_normalized
          dataTableId: publications_data_table
        layoutProperties:
          fraction: 0.166666
          height: 350
      widgetPaper_journal_title:
        chartType: FacetCategorical
        config:
          simpleLayout: false
        dataBinding:
          aspectBindings:
            categories: journal_name
          dataTableId: publications_data_table
        layoutProperties:
          fraction: 1
          height: 350

      embedding_umap:
          chartType: ScatterPlot
          dataBinding:
            aspectBindings:
              color: topics
              hoverLabel: journal_name
              x: umap_x
              y: umap_y
            dataTableId: publications_data_table
          config:
            opacity: 0.5
            hideAxes: true
            hideColorLegend: true
            pointSizeFactor: 1.25
            colorSchemeCategorical: "Default 20"
          layoutProperties:
            fraction: 0.6
            height: 600
          title: UMAP Visualization of Research Papers 

      facet_topic:
        chartType: ColorLegendCategorical
        dataBinding:
          aspectBindings:
            categories: topics
          dataTableId: publications_data_table
        config:
          categoriesSchemeCategorical: "Default 20"
        layoutProperties:
          fraction: 0.4
          height: 600
        title: Research Topics


  templateId: default
  version: 0.1.0
templateScope: dashboard
templateType: bioreflect_pipeline

`;

  return dashboardYaml;
};

export const generateEntityYaml = () => {
  return `
templateContent:
  assetType: bioreflect_pipeline
  displayName: Bio|verse Entity for bioreflect_results
  entities:
  - displayName: Publication
    elements:
    - title: Properties
      type: propertyList
      propsToShow:
      - title
      - journal_name
      - abstract
      - authors
      - publication_year
      - citations
      hideAbsentProperties: true
      propConfig:
        - id: authors
          useBadge: true
    - columnsTo:
      - id: gene_name
        width: 100
        bold: true
      - id: ensembl_id
        width: 150
      - id: first_publication
        width: 150
      - id: evidence_summary
        width: 450
      linkFrom: mentioned_genes
      linkTo: gene_name
      tableFrom: publications_data_table
      tableTo: genes_data_table
      title: Genes mentioned in this publication
      type: relatedTable
    entityType: etl_publications

  - displayName: Gene
    elements:
    - propsToShow:
      - gene_name
      - ensembl_id
      - gene_associations
      - confidence_level
      - evidence_summary
      - first_publication
      title: Properties
      type: propertyList
      hideAbsentProperties: true
      propConfig:
        - id: gene_associations
          useBadge: true
    - columnsTo:
      - id: id
        width: 100
      - bold: true
        id: title
        width: 400
      - id: article_on_disease
        width: 100
      - id: publication_year
        width: 100
      - id: journal_name
        width: 200
      linkFrom: linked_publications
      linkTo: id
      tableFrom: genes_data_table
      tableTo: publications_data_table
      title: Publications this gene is mentioned in
      type: relatedTable
    entityType: etl_genes
  templateId: default
  version: 0.1.0
templateScope: entity
templateType: bioreflect_pipeline
`;
};
